import type { RuntimeOutboxEvent, RuntimeOutboxEventType } from './types';

const STORAGE_KEY = 'belajarku:runtime-outbox:v1';
export const RUNTIME_OUTBOX_UPDATED_EVENT = 'belajarku:runtime-outbox:updated';
const BASE_RETRY_MS = 5_000;
const MAX_RETRY_MS = 5 * 60_000;

export function loadRuntimeOutbox(): RuntimeOutboxEvent[] {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const events = JSON.parse(raw) as RuntimeOutboxEvent[];
    return Array.isArray(events) ? events : [];
  } catch {
    return [];
  }
}

function saveRuntimeOutbox(events: RuntimeOutboxEvent[]) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  window.dispatchEvent(new CustomEvent(RUNTIME_OUTBOX_UPDATED_EVENT));
}

export function enqueueRuntimeEvent(input: {
  id: string;
  eventType: RuntimeOutboxEventType;
  studentId: string;
  payload: unknown;
}) {
  if (typeof window === 'undefined') return;
  const current = loadRuntimeOutbox();
  if (current.some((item) => item.id === input.id)) return;
  const now = new Date().toISOString();
  const event: RuntimeOutboxEvent = {
    ...input,
    status: 'pending',
    retryCount: 0,
    createdAt: now,
    updatedAt: now,
  };
  saveRuntimeOutbox([...current, event]);
}

export function pendingRuntimeEventCount() {
  const now = Date.now();
  return loadRuntimeOutbox().filter((event) => {
    if (event.status === 'pending' || event.status === 'syncing') return true;
    if (event.status !== 'failed') return false;
    return !event.nextRetryAt || Date.parse(event.nextRetryAt) <= now;
  }).length;
}

export function isRuntimeEventReadyForRetry(event: RuntimeOutboxEvent, now = Date.now()) {
  if (event.status === 'pending') return true;
  if (event.status !== 'failed') return false;
  return !event.nextRetryAt || Date.parse(event.nextRetryAt) <= now;
}

export function markRuntimeEventSyncing(id: string) {
  const now = new Date().toISOString();
  saveRuntimeOutbox(loadRuntimeOutbox().map((event) => event.id === id ? {
    ...event,
    status: 'syncing',
    updatedAt: now,
    lastError: undefined,
  } : event));
}

export function markRuntimeEventSynced(id: string) {
  const now = new Date().toISOString();
  saveRuntimeOutbox(loadRuntimeOutbox().map((event) => event.id === id ? {
    ...event,
    status: 'synced',
    updatedAt: now,
    syncedAt: now,
    nextRetryAt: undefined,
    lastError: undefined,
  } : event));
}

export function markRuntimeEventLocalOnly(id: string, reason = 'Browser-local pilot event; tidak dikirim ke cloud.') {
  const now = new Date().toISOString();
  saveRuntimeOutbox(loadRuntimeOutbox().map((event) => event.id === id ? {
    ...event,
    status: 'local_only',
    updatedAt: now,
    nextRetryAt: undefined,
    lastError: reason,
  } : event));
}

export function markRuntimeEventFailed(id: string, message: string) {
  const nowMs = Date.now();
  const now = new Date(nowMs).toISOString();
  saveRuntimeOutbox(loadRuntimeOutbox().map((event) => {
    if (event.id !== id) return event;
    const retryCount = event.retryCount + 1;
    const delayMs = Math.min(MAX_RETRY_MS, BASE_RETRY_MS * (2 ** Math.min(retryCount - 1, 8)));
    return {
      ...event,
      status: 'failed',
      retryCount,
      lastError: message,
      nextRetryAt: new Date(nowMs + delayMs).toISOString(),
      updatedAt: now,
    };
  }));
}

export function recoverInterruptedRuntimeEvents() {
  const now = new Date().toISOString();
  const events = loadRuntimeOutbox();
  let changed = false;
  const recovered = events.map((event) => {
    if (event.status !== 'syncing') return event;
    changed = true;
    return { ...event, status: 'pending' as const, updatedAt: now, lastError: 'Sync sebelumnya terputus; aman untuk dicoba ulang.' };
  });
  if (changed) saveRuntimeOutbox(recovered);
}

export function pruneRuntimeOutbox(options: { syncedOlderThanMs?: number; keepNewest?: number } = {}) {
  const syncedOlderThanMs = options.syncedOlderThanMs ?? 7 * 24 * 60 * 60_000;
  const keepNewest = options.keepNewest ?? 200;
  const threshold = Date.now() - syncedOlderThanMs;
  const current = loadRuntimeOutbox();
  const retained = current
    .filter((event) => event.status !== 'synced' || !event.syncedAt || Date.parse(event.syncedAt) >= threshold)
    .slice(-keepNewest);
  if (retained.length !== current.length) saveRuntimeOutbox(retained);
  return current.length - retained.length;
}
